import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { X, Download, Sparkles, Loader2 } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { api } from "@/lib/api";

const MOOD_EMOJI = {
  happy: "😊", calm: "😌", grateful: "🥰", hopeful: "🥺",
  tired: "😴", sad: "😔", anxious: "😣", frustrated: "😤",
};

export default function WeeklyRecap({ onClose }) {
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    api.get("/weekly-recap").then((r) => setData(r.data)).catch(() => toast.error("Couldn't load your recap"));
  }, []);

  const download = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const url = await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#FFF9F9" });
      const a = document.createElement("a");
      a.href = url;
      a.download = `weekly-recap-${data?.end || 'week'}.png`;
      a.click();
      toast.success("Saved to downloads ♡");
    } catch (e) {
      toast.error("Couldn't create image");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 backdrop-blur-xl bg-white/40 flex items-center justify-center p-4 overflow-y-auto"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="w-full max-w-lg my-8"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-kawaii-textSoft font-semibold">Weekly recap</p>
            <h2 className="font-heading text-2xl font-semibold text-kawaii-text">Your gentle week ♡</h2>
          </div>
          <button
            onClick={onClose}
            data-testid="close-recap-btn"
            className="kawaii-btn w-10 h-10 rounded-full bg-white border border-kawaii-border flex items-center justify-center"
          >
            <X className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </div>

        {!data ? (
          <div className="kawaii-card p-8 text-center text-kawaii-textSoft">
            <Loader2 className="w-6 h-6 mx-auto animate-spin" />
            <p className="mt-3 font-body">Gathering your week...</p>
          </div>
        ) : (
          <>
            <div
              ref={cardRef}
              data-testid="weekly-recap-card"
              className="relative rounded-[2rem] overflow-hidden border border-kawaii-border p-8"
              style={{
                background: 'linear-gradient(135deg, #FFE5E5 0%, #FFF9F9 40%, #E2F0CB 100%)'
              }}
            >
              <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-kawaii-pink/30 blur-2xl" />
              <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-kawaii-lavender/30 blur-2xl" />

              <div className="relative">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-4 h-4 text-kawaii-textSoft" strokeWidth={2.5} />
                  <span className="text-[10px] uppercase tracking-[0.25em] text-kawaii-textSoft font-bold">MyLymphieLife</span>
                </div>
                <h1 className="font-heading text-3xl font-semibold text-kawaii-text leading-tight">
                  {format(parseISO(data.start), "MMM d")} – {format(parseISO(data.end), "MMM d, yyyy")}
                </h1>

                {data.entries === 0 ? (
                  <p className="mt-6 font-body text-kawaii-text leading-relaxed">{data.summary}</p>
                ) : (
                  <>
                    <div className="mt-6 grid grid-cols-4 gap-2">
                      <div className="rounded-2xl bg-white/70 p-3 text-center">
                        <div className="text-2xl">{data.top_mood ? MOOD_EMOJI[data.top_mood] || "💗" : "🌱"}</div>
                        <div className="text-[9px] uppercase tracking-wider text-kawaii-textSoft font-bold mt-1">top mood</div>
                      </div>
                      <div className="rounded-2xl bg-white/70 p-3 text-center">
                        <div className="font-heading text-xl text-kawaii-text font-semibold">{data.avg_pain}</div>
                        <div className="text-[9px] uppercase tracking-wider text-kawaii-textSoft font-bold mt-1">avg pain</div>
                      </div>
                      <div className="rounded-2xl bg-white/70 p-3 text-center">
                        <div className="font-heading text-xl text-kawaii-text font-semibold">{data.avg_swelling}</div>
                        <div className="text-[9px] uppercase tracking-wider text-kawaii-textSoft font-bold mt-1">avg swell</div>
                      </div>
                      <div className="rounded-2xl bg-white/70 p-3 text-center">
                        <div className="font-heading text-xl text-kawaii-text font-semibold">{data.flare_days}</div>
                        <div className="text-[9px] uppercase tracking-wider text-kawaii-textSoft font-bold mt-1">flare days</div>
                      </div>
                    </div>

                    <div className="mt-4 rounded-2xl bg-white/70 p-4">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-kawaii-textSoft font-bold mb-2">Mood trail</div>
                      <div className="flex justify-between">
                        {data.moods.map((m) => (
                          <div key={m.date} className="text-center">
                            <div className="text-2xl">{m.emoji ? MOOD_EMOJI[m.emoji] || "·" : "·"}</div>
                            <div className="text-[9px] text-kawaii-textSoft mt-0.5">{format(parseISO(m.date), "EEE")}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <p className="mt-5 font-body text-sm text-kawaii-text leading-relaxed italic">
                      {data.summary}
                    </p>

                    <div className="mt-5 flex items-center justify-between text-[10px] uppercase tracking-widest text-kawaii-textSoft font-bold">
                      <span>{data.entries} entries · {data.self_care_score} self-care wins</span>
                      <span>♡</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={download} disabled={downloading}
                data-testid="download-recap-btn"
                className="kawaii-btn px-5 py-2.5 rounded-full bg-gradient-to-r from-kawaii-pink to-kawaii-peach text-white font-body font-semibold text-sm shadow-md inline-flex items-center gap-2 disabled:opacity-60"
              >
                {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" strokeWidth={2.5} />}
                Save as image
              </button>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}
